import styled from '@emotion/styled';
import { Col, Row, Typography } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, Link, useLocation } from 'react-router';

import { PATH } from 'constants/RoutesConstants';
import { useSelector } from 'hooks/useSelector';
import { hasAppError as hasAppErrorSelector } from 'modules/app/selectors';
import type { MayBe } from 'modules/global/typing/common/common';
import { haveInitializeSession } from 'modules/users/selectors/currentUser';

import Button from 'ui/base/Button';

const { Title } = Typography;

type PageErrorLocationState = {
  dashboard?: { path: string };
};

type PageErrorProps = {
  title?: React.ReactNode;
  message?: React.ReactNode;
};

const RowPage = styled(Row)`
  min-height: 100vh;

  > .ant-col {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const PageError: React.FC<PageErrorProps> = ({
  title = <FormattedMessage id="page.error.title" defaultMessage="Oops" />,
  message = <FormattedMessage id="error.fetch" defaultMessage="An error occurred during the data fetching." />,
}) => {
  const { state } = useLocation();
  const dashboard = (state as MayBe<PageErrorLocationState>)?.dashboard;

  const hasAppError = useSelector(hasAppErrorSelector);
  const isSessionReady = useSelector(haveInitializeSession);

  const buttonText = React.useMemo(() => {
    if (!hasAppError && isSessionReady) {
      return dashboard?.path ? (
        <FormattedMessage id="page.error.buttons.dashboard" defaultMessage="Return to dashboard" />
      ) : (
        <FormattedMessage id="page.error.buttons.homepage" defaultMessage="Return to home page" />
      );
    }
    return <FormattedMessage id="refreshPage" defaultMessage="Refresh the page" />;
  }, [dashboard?.path, hasAppError, isSessionReady]);

  const path = generatePath((dashboard ?? PATH.HOME).path);

  const dataCy = dashboard ? 'back-dashboard' : 'back-homepage';

  return (
    <RowPage justify="center" align="middle">
      <Col span={12}>
        <div>
          <Title level={2}>{title}</Title>
          <p>{message}</p>
          <Link to={path} reloadDocument={hasAppError || !isSessionReady}>
            <Button type="primary" ghost data-cy={dataCy}>
              {buttonText}
            </Button>
          </Link>
        </div>
      </Col>
    </RowPage>
  );
};

export default PageError;
